import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ConfigService } from '@app/core/services/config.service';
import { environment } from '@env/environment';
import { Hub } from '../models/hub';
import { HubSearchParams } from '../models/hub-search-params';
import { HubDetails } from '../models/hub-details';
import { map, Observable } from 'rxjs';
import { HubSchedule } from '../models/hub-schedule';
import { Schedule } from '../models/schedule';
import { DayOfWeek } from '../models/day-of-week';
import { CreateHub } from '../models/create-hub';
import { CreateHubResult } from '../models/create-hub-result';
import { UpdateHub } from '../models/update-hub';
import { HubUpdateResult } from '../models/update-hub-result';

@Injectable({
  providedIn: 'root'
})
export class HubService {
  haveErrors: boolean = false;
  private readonly SearchHubUrl: string;
  private readonly HubDetailsGetUrl: string;
  private readonly HubCreateUrl: string;
  private readonly UserNumberExistsUrl: string;
  private readonly HubNameExistsUrl: string;
  private readonly HubAddressExistsUrl: string;

  constructor(private configService: ConfigService, private httpClient: HttpClient) {
    this.SearchHubUrl = `${environment.portalGatewayUrl}/${this.configService.getSettings('cleard.portalGateway.hub.search')}`;
    this.HubDetailsGetUrl = `${environment.portalGatewayUrl}/${this.configService.getSettings('cleard.portalGateway.hub.getHubDetails')}`;
    this.HubCreateUrl = `${environment.portalGatewayUrl}/${this.configService.getSettings('cleard.portalGateway.hub.create')}`;
    this.UserNumberExistsUrl = `${environment.portalGatewayUrl}/${this.configService.getSettings('cleard.portalGateway.hub.userNumberExists')}`;
    this.HubNameExistsUrl = `${environment.portalGatewayUrl}/${this.configService.getSettings('cleard.portalGateway.hub.hubNameExists')}`;
    this.HubAddressExistsUrl = `${environment.portalGatewayUrl}/${this.configService.getSettings('cleard.portalGateway.hub.hubsMatchingAddress')}`;
  }

  search(hubSearchParams: HubSearchParams): Observable<Hub[]> {
    return this.httpClient.post<Hub[]>(this.SearchHubUrl, hubSearchParams);
  }

  getDetails(hubId: number): Observable<HubDetails> {
    return this.httpClient.get<any>(this.HubDetailsGetUrl.replace('{hubId}', hubId.toString())).pipe(map(response => {
      return {
        id: response.id,
        friendlyName: response.friendlyName,
        isCrossDockAllowed: response.isCrossDockAllowed,
        isOvernightReceptionAllowed: response.isOvernightReceptionAllowed,
        address: {
          city: response.address.city,
          street: response.address.street,
          unit: response.address.aptUnit,
          doorNumber: response.address.doorNumber,
          postalCode: response.address.postalCode,
          provinceId: response.address.province.id
        },
        commonLocationTypeId: response.commonLocationTypeId,
        geoPoint: response.geoPoint,
        isDefaultHolidays: response.isDefaultHolidays,
        hubType: response.hubType,
        shippingSchedule: this.mapHubScheduleToSchedule(response.shippingSchedule),
        receivingSchedule: this.mapHubScheduleToSchedule(response.receivingSchedule),
        internalSchedule: this.mapHubScheduleToSchedule(response.internalProcessSchedule),
        lastMileSchedule: this.mapHubScheduleToSchedule(response.lastMileSchedule),
        closedDays: response.closedDays,
        hubLinksFrom: response.hubLinksFrom,
        hubLinksTo: response.hubLinksTo,
        commonLocationSummaries: response.commonLocationSummaries,
        vendorIds: response.vendorIds,
        storeNumber: response.storeNumber,
        isShippingPlanningView: response.isShippingPlanningView
      };
    }));
  }

  create(hub: CreateHub): Observable<CreateHubResult> {
    return this.httpClient.post<CreateHubResult>(this.HubCreateUrl, hub);
  }

  update(hub: UpdateHub): Observable<HubUpdateResult> {
    return this.httpClient.put<HubUpdateResult>(this.HubCreateUrl, hub);
  }

  doesUserNumberExist(userNumber: string): Observable<boolean> {
    let queryParams = new HttpParams();
    queryParams = queryParams.append('userNumber', userNumber);
    return this.httpClient.get<boolean>(this.UserNumberExistsUrl, { params: queryParams });
  }

  doesHubNameExist(hubName: string): Observable<boolean> {
    let queryParams = new HttpParams();
    queryParams = queryParams.append('hubName', hubName);
    return this.httpClient.get<boolean>(this.HubNameExistsUrl, { params: queryParams });
  }

  getHubsWithMatchingAddress(doorNumber: string, postalCode: string): Observable<number[]> {
    let queryParams = new HttpParams();
    queryParams = queryParams.append('doorNumber', doorNumber);
    queryParams = queryParams.append('postalCode', postalCode);
    return this.httpClient.get<number[]>(this.HubAddressExistsUrl, { params: queryParams });
  }

  private mapHubScheduleToSchedule(hubSchedules: HubSchedule[]): Schedule {
    if (!hubSchedules.length) {
      return {
        isMonday: false,
        isTuesday: false,
        isWednesday: false,
        isThursday: false,
        isFriday: false,
        isSaturday: false,
        isSunday: false
      };
    } else {
      const schedule = hubSchedules[0].open;
      return {
        isMonday: schedule.includes(DayOfWeek.Monday),
        isTuesday: schedule.includes(DayOfWeek.Tuesday),
        isWednesday: schedule.includes(DayOfWeek.Wednesday),
        isThursday: schedule.includes(DayOfWeek.Thursday),
        isFriday: schedule.includes(DayOfWeek.Friday),
        isSaturday: schedule.includes(DayOfWeek.Saturday),
        isSunday: schedule.includes(DayOfWeek.Sunday)
      };
    }
  }
}
